/**
 * Usage & Cost.
 *
 * Replaces the single-page dashboard that could report what we spent by model
 * and by day, and could not report the things a pricing decision needs: what a
 * phone minute costs against what we charge for one, whether an agent's phone
 * channel is dearer than its screen channel, where the margin is.
 *
 * The structure follows the questions rather than the tables:
 *
 *   Overview    what did this slice cost, and what moved
 *   Comparison  which channel, agent or model is expensive per unit
 *   Sessions    which individual conversation is the outlier, and why
 *   Rate card   every price in the system, cost side next to price side
 *
 * One filter set drives all four, and it lives in the URL — so the slice that
 * produced a number can be sent to someone else, which is the difference
 * between a finding and an anecdote.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Activity, BarChart3, Calculator, Layers, ListOrdered, Tag } from 'lucide-react';
import { useAdminAuth } from '../../contexts/AdminAuthContext';
import { AdminAPI, UsageReportSummary } from '../../lib/api';
import { FilterBar, useUsageFilters } from './FilterBar';
import { OverviewTab } from './OverviewTab';
import { ChannelsTab } from './ChannelsTab';
import { SessionsTab } from './SessionsTab';
import { RateCardTab } from './RateCardTab';
import { CogsCalculator } from './CogsCalculator';
import { CurrencyContext, CurrencyToggle, Money, Notice } from './shared';

type TabKey = 'overview' | 'channels' | 'sessions' | 'rates';

const TABS: [TabKey, string, React.ComponentType<{ className?: string }>, string][] = [
  ['overview', 'Overview', BarChart3, 'Spend, trend and where it goes'],
  ['channels', 'Channels', Layers, 'COGS per call, per session, per message'],
  ['sessions', 'Sessions', ListOrdered, 'Every conversation, and its ledger'],
  ['rates', 'Rate card', Tag, 'What it costs us, what we charge'],
];

export const Usage: React.FC = () => {
  const { token } = useAdminAuth();
  const api = useMemo(() => new AdminAPI(token!), [token]);
  const { filters, patch, preset, tab, ccy } = useUsageFilters();
  const [cogsOpen, setCogsOpen] = useState(false);

  const [summary, setSummary] = useState<UsageReportSummary | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Bumped by Refresh. Tabs watch it so a manual refresh re-fetches their own
  // data too, without each of them owning a copy of the filter state.
  const [nonce, setNonce] = useState(0);

  // 'comparison' links from before the redesign land on Channels.
  const active = (TABS.find(([k]) => k === tab)?.[0] ?? (tab === 'comparison' ? 'channels' : 'overview')) as TabKey;
  const currency = useMemo(
    () => ({ ccy, rate: summary?.fx?.rate ?? null, asOf: summary?.fx?.as_of ?? null }),
    [ccy, summary?.fx?.rate, summary?.fx?.as_of],
  );

  useEffect(() => {
    setLoading(true);
    api.getUsageReportSummary(filters)
      .then((s) => { setSummary(s); setError(null); })
      .catch((e) => setError(`Couldn't load the summary: ${e instanceof Error ? e.message : String(e)}. Refresh to try again.`))
      .finally(() => setLoading(false));
  }, [api, filters, nonce]);

  const refresh = useCallback(() => setNonce((n) => n + 1), []);
  const drill = useCallback(
    (p: Record<string, string | undefined>) => patch(p),
    [patch],
  );

  // Spend per agent, so the agent picker can rank by what each one costs in
  // the CURRENT window rather than alphabetically.
  const [agentCost, setAgentCost] = useState<Record<string, number>>({});
  useEffect(() => {
    api.getUsageBreakdown(filters, 'agent', 200)
      .then((b) => {
        const map: Record<string, number> = {};
        for (const r of b.rows) if (r.key) map[r.key] = r.cost_usd;
        setAgentCost(map);
      })
      .catch(() => setAgentCost({}));
  }, [api, filters, nonce]);

  return (
    <CurrencyContext.Provider value={currency}>
    <div className="max-w-[1600px] space-y-4">
      <header className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-3">
          <Activity className="h-7 w-7 text-blue-600" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Usage &amp; Cost</h1>
            <p className="text-xs text-gray-500">
              Every LLM, speech and carrier-minute charge, priced per event and rolled up per
              session — live, including today.
            </p>
          </div>
        </div>
        <div className="flex items-start gap-4">
          <div className="flex flex-col items-end gap-1.5">
            <CurrencyToggle onChange={(c) => patch({ ccy: c === 'INR' ? 'inr' : undefined })} />
            <button
              onClick={() => setCogsOpen(true)}
              className="inline-flex items-center gap-1.5 rounded-md border border-gray-900 bg-gray-900 px-2.5 py-1 text-xs font-medium text-white hover:bg-gray-800"
              title="Monthly cost of goods sold from this window's unit costs — every assumption editable, export to CSV"
            >
              <Calculator className="h-3.5 w-3.5" /> COGS calculator
            </button>
          </div>
          {summary && (
            <div className="hidden text-right sm:block">
              <div className="text-2xl font-semibold tabular-nums text-gray-900">
                <Money value={summary.totals.cost_usd} inr={summary.totals.cost_inr} />
              </div>
              <div className="text-[11px] text-gray-500">
                {filters.from} → {filters.to}
              </div>
            </div>
          )}
        </div>
      </header>

      <FilterBar
        api={api}
        filters={filters}
        preset={preset}
        patch={patch}
        onRefresh={refresh}
        loading={loading}
        agentCost={agentCost}
      />

      {error && <Notice tone="error">{error}</Notice>}

      <nav className="flex gap-1 border-b border-gray-200">
        {TABS.map(([key, label, Icon, hint]) => (
          <button
            key={key}
            onClick={() => patch({ tab: key })}
            title={hint}
            className={`-mb-px flex items-center gap-1.5 border-b-2 px-3 py-2 text-sm font-medium transition-colors ${
              active === key
                ? 'border-blue-600 text-blue-700'
                : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
            }`}
          >
            <Icon className="h-3.5 w-3.5" />
            {label}
          </button>
        ))}
      </nav>

      {active === 'overview' && (
        <OverviewTab api={api} filters={filters} summary={summary} nonce={nonce} onDrill={drill} />
      )}
      {active === 'channels' && (
        <ChannelsTab api={api} filters={filters} summary={summary} nonce={nonce} onDrill={drill} />
      )}
      {active === 'sessions' && <SessionsTab api={api} filters={filters} nonce={nonce} />}
      {active === 'rates' && <RateCardTab api={api} nonce={nonce} />}

      <CogsCalculator open={cogsOpen} onClose={() => setCogsOpen(false)} summary={summary} filters={filters} />
    </div>
    </CurrencyContext.Provider>
  );
};

export default Usage;
